import { api } from './_client';
import type {
  MaintenanceDocumentListResponse,
  MaintenanceDocumentUploadResponse,
  MaintenanceDocumentDeleteResponse,
  DocumentProcessResponse,
} from '../../types/api';

export const maintenanceDocsApi = {
  // Upload a maintenance document (PDF)
  async uploadMaintenanceDocument(
    file: File,
    documentTitle: string,
    documentType: string,
    documentVersion?: string,
    droneModel?: string,
    manufacturer?: string
  ): Promise<MaintenanceDocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_title', documentTitle);
    formData.append('document_type', documentType);
    if (documentVersion) formData.append('document_version', documentVersion);
    if (droneModel) formData.append('drone_model', droneModel);
    if (manufacturer) formData.append('manufacturer', manufacturer);

    const response = await api.post<MaintenanceDocumentUploadResponse>(
      '/api/admin/maintenance-documents/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for large PDFs
      }
    );
    return response.data;
  },

  // List all maintenance documents
  async listMaintenanceDocuments(): Promise<MaintenanceDocumentListResponse> {
    const response = await api.get<MaintenanceDocumentListResponse>(
      '/api/admin/maintenance-documents'
    );
    return response.data;
  },

  // Delete a maintenance document
  async deleteMaintenanceDocument(s3Key: string): Promise<MaintenanceDocumentDeleteResponse> {
    const response = await api.delete<MaintenanceDocumentDeleteResponse>(
      `/api/admin/maintenance-documents/${encodeURIComponent(s3Key)}`
    );
    return response.data;
  },

  // Process a maintenance document (manual processing)
  async processMaintenanceDocument(s3Key: string): Promise<DocumentProcessResponse> {
    const response = await api.post<DocumentProcessResponse>(
      `/api/admin/maintenance-documents/${encodeURIComponent(s3Key)}/process`,
      {},
      {
        timeout: 300000, // 5 minutes for document processing
      }
    );
    return response.data;
  },

  // Get presigned URL for maintenance manual
  async getManualUrl(filename: string): Promise<{ url: string; expires_in: number }> {
    const response = await api.get<{ url: string; expires_in: number }>(`/api/manuals/${filename}`);
    return response.data;
  },

  // Get presigned URL for a rendered page image from a knowledge base document
  async getPageImageUrl(
    sourceId: string,
    page: number
  ): Promise<{ url: string | null; expires_in: number }> {
    const response = await api.get<{ url: string | null; expires_in: number }>(
      '/api/knowledge/page-image',
      { params: { source_id: sourceId, page } }
    );
    return response.data;
  },
};
